import { useEffect, useState } from "react";
import { Upload, FileText, AlertTriangle, Building2, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate, API } from "../lib/api";
import { EmptyState } from "../components/EmptyState";


/**
 * Owner Upload Centre — one page for outstanding uploads.
 *
 *  · Vault gaps per site (agreements, licences, floor plans, NOCs) with inline
 *    upload picker: title + category dropdown + file → POST to
 *    /api/sites/{id}/documents.
 *  · FH monthly reports awaiting signature — inline picker → POST to
 *    /api/fh-reports/{id}/upload-signed.
 *  · Open review flags — inline "Resolve" button → PATCH review-flags/{id}.
 */
export default function UploadCentrePage() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    async function refresh() {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/upload-centre/pending");
            setData(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }
    useEffect(() => { refresh(); }, []);

    async function uploadVaultDoc(site_id, category, file, title) {
        if (!file || !title?.trim()) return;
        const form = new FormData();
        form.append("title", title.trim());
        form.append("category", category);
        form.append("access_level", "HO_ONLY");
        form.append("file", file);
        try {
            await api.post(`/sites/${site_id}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Uploaded ${title}`);
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function uploadSignedFhReport(rid, file) {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        try {
            await api.post(`/fh-reports/${rid}/upload-signed`, form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Signed scan uploaded");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function resolveFlag(fid) {
        try {
            await api.patch(`/review-flags/${fid}`, { status: "resolved" });
            toast.success("Flag resolved");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    }

    if (!data) {
        return <div className="mx-auto max-w-5xl p-6"><div className="h-72 rounded-xl bg-white animate-pulse" /></div>;
    }

    const c = data.counts || {};
    const nothingPending = c.vault_total_gaps === 0 && c.fh_reports_awaiting === 0 && c.open_flags === 0;

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10 space-y-8" data-testid="upload-centre">
            <header className="flex items-center justify-between">
                <div>
                    <div className="text-xs font-medium uppercase tracking-widest text-[#E02B2B]">Owner portal</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Upload centre</h1>
                    <p className="text-sm text-gray-600 mt-1">One page for every outstanding upload — vault documents, signed FH reports, and open review flags.</p>
                </div>
                <button onClick={refresh} disabled={busy} className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800" data-testid="uc-refresh">
                    <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
                </button>
            </header>

            {/* Pending checklist strip */}
            <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="uc-counts">
                <CountCard icon={<Building2 className="w-5 h-5" />} label="Sites with vault gaps" value={c.vault_sites_with_gaps ?? 0} sub={`${c.vault_total_gaps ?? 0} category gap(s)`} testid="uc-count-vault" />
                <CountCard icon={<FileText className="w-5 h-5" />} label="FH reports awaiting signature" value={c.fh_reports_awaiting ?? 0} testid="uc-count-fh" />
                <CountCard icon={<AlertTriangle className="w-5 h-5" />} label="Open review flags" value={c.open_flags ?? 0} testid="uc-count-flags" />
            </section>

            {nothingPending && (
                <EmptyState icon="✅" title="All caught up" subtitle="No pending uploads or review flags at the moment." />
            )}

            {/* Vault gaps */}
            {data.vault_gaps?.length > 0 && (
                <section data-testid="uc-vault-section">
                    <h2 className="font-display text-sm font-bold uppercase tracking-widest text-[#1E3355] mb-3 inline-flex items-center gap-2">
                        <Building2 className="w-4 h-4" /> Vault documents — sites with gaps
                    </h2>
                    <ul className="rounded-xl border border-gray-100 bg-white divide-y divide-gray-100 overflow-hidden">
                        {data.vault_gaps.map((s) => (
                            <li key={s.site_id} className="p-4" data-testid={`uc-vault-${s.site_id}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <div className="font-semibold text-[#1E3355]">{s.site_name}</div>
                                        <div className="text-xs text-gray-500">{s.site_type} · {s.missing_count} category gap(s)</div>
                                    </div>
                                </div>
                                <ul className="space-y-2">
                                    {s.missing_categories.map((cat) => (
                                        <VaultUploadRow
                                            key={cat.key}
                                            site_id={s.site_id}
                                            category={cat.key}
                                            categoryLabel={cat.label}
                                            onSubmit={uploadVaultDoc}
                                        />
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* FH awaiting signature */}
            {data.fh_reports_awaiting_signature?.length > 0 && (
                <section data-testid="uc-fh-section">
                    <h2 className="font-display text-sm font-bold uppercase tracking-widest text-[#1E3355] mb-3 inline-flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Signed FH report scans — awaiting upload
                    </h2>
                    <ul className="rounded-xl border border-gray-100 bg-white divide-y divide-gray-100 overflow-hidden">
                        {data.fh_reports_awaiting_signature.map((r) => (
                            <li key={r.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`uc-fh-${r.id}`}>
                                <div className="flex-1 min-w-[220px]">
                                    <div className="font-medium text-[#1E3355]">{r.fh_name}</div>
                                    <div className="text-xs text-gray-500">{r.site_name} · {r.month} · generated {r.generated_at ? fmtDate(r.generated_at) : "—"}</div>
                                </div>
                                <a
                                    href={`${API}/fh-reports/${r.id}/pdf`}
                                    className="px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-xs font-semibold"
                                    data-testid={`uc-fh-view-${r.id}`}
                                >View generated</a>
                                <label className="px-3 py-1.5 rounded-md bg-[#1E3355] hover:bg-[#26406b] text-white text-xs font-semibold inline-flex items-center gap-1 cursor-pointer" data-testid={`uc-fh-upload-${r.id}`}>
                                    <Upload className="w-3.5 h-3.5" /> Upload signed scan
                                    <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => uploadSignedFhReport(r.id, e.target.files?.[0])} />
                                </label>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Open review flags */}
            {data.open_review_flags?.length > 0 && (
                <section data-testid="uc-flags-section">
                    <h2 className="font-display text-sm font-bold uppercase tracking-widest text-[#1E3355] mb-3 inline-flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" /> Open review flags
                    </h2>
                    <ul className="rounded-xl border border-gray-100 bg-white divide-y divide-gray-100 overflow-hidden">
                        {data.open_review_flags.map((f) => (
                            <li key={f.id} className="p-3 flex items-center gap-3" data-testid={`uc-flag-${f.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-[#1E3355] truncate">{f.reason || "(no reason)"}</div>
                                    <div className="text-xs text-gray-500">{f.site_name || "—"} · {f.month || "—"} · raised {f.created_at ? fmtDate(f.created_at) : "—"}</div>
                                </div>
                                <button onClick={() => resolveFlag(f.id)} className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1" data-testid={`uc-flag-resolve-${f.id}`}>
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}


function CountCard({ icon, label, value, sub, testid }) {
    return (
        <div className="rounded-xl bg-white border border-gray-100 p-4 flex items-center gap-3" data-testid={testid}>
            <div className="w-10 h-10 rounded-full bg-[#1E3355]/10 text-[#1E3355] flex items-center justify-center">{icon}</div>
            <div>
                <div className="text-xs text-gray-500">{label}</div>
                <div className="font-display text-2xl font-bold text-[#1E3355]">{value}</div>
                {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
            </div>
        </div>
    );
}


function VaultUploadRow({ site_id, category, categoryLabel, onSubmit }) {
    const [title, setTitle] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    async function handle() {
        if (!title.trim() || !file) return;
        setUploading(true);
        await onSubmit(site_id, category, file, title);
        setUploading(false);
        setTitle(""); setFile(null);
    }

    return (
        <li className="flex flex-wrap items-center gap-2 border border-gray-100 rounded-md p-2" data-testid={`uc-vault-row-${site_id}-${category}`}>
            <div className="text-xs font-semibold text-[#1E3355] w-full sm:w-48">{categoryLabel}</div>
            <input
                type="text"
                placeholder="Title (e.g. Agreement 2026)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="flex-1 min-w-[140px] border border-gray-300 rounded-md px-2 py-1 text-xs"
                data-testid={`uc-vault-title-${site_id}-${category}`}
            />
            <input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid={`uc-vault-file-${site_id}-${category}`}
            />
            <button
                onClick={handle}
                disabled={!title.trim() || !file || uploading}
                className="px-2.5 py-1 rounded-md bg-[#1E3355] hover:bg-[#26406b] text-white text-xs font-semibold disabled:opacity-50"
                data-testid={`uc-vault-upload-${site_id}-${category}`}
            >{uploading ? "…" : "Upload"}</button>
        </li>
    );
}
